import React, { useEffect } from 'react';
import { NEXT_CONFIG } from '@common/constants/config/nextConfig';

import { createGlobalStyle, css } from 'styled-components';
import { ThemeType } from '@legacyApp/client/modules/style/theme';
import { useHotjarControl } from '@modules/hotjar/hooks/useHotjarControl';
import { useGameActivityDetector } from '@modules/hotjar/hooks/useGameActivityDetector';
import { PERIODIC_STATUS_INTERVAL } from '@modules/hotjar/constants';

const HotjarManager: React.FC = () => {
	const { state, startRecording, stopRecording } = useHotjarControl();

	// Set up game activity detection with all game-related logic
	useGameActivityDetector({
		isRecording: state.isRecording,
		stopRecording,
		startRecording,
	});

	// Initialize Hotjar script
	useEffect(() => {
		if (
			!NEXT_CONFIG.NEXT_PUBLIC_HOTJAR_ID ||
			NEXT_CONFIG.NEXT_PUBLIC_HOTJAR_ID === ''
		) {
			return;
		}

		if (
			typeof window !== 'undefined' &&
			!window.hj &&
			NEXT_CONFIG.NEXT_PUBLIC_HOTJAR_ID
		) {
			const script = document.createElement('script');
			script.innerHTML = `
        (function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:${NEXT_CONFIG.NEXT_PUBLIC_HOTJAR_ID},hjsv:6};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
      `;
			document.head.appendChild(script);

			setTimeout(() => {
				if (window.hj) {
					// Clear opt-out state if present
					const isOptedOut =
						(typeof window.hj.optOut === 'function' && window.hj.optOut()) ||
						(typeof window.hj.optOut === 'boolean' && window.hj.optOut);

					if (isOptedOut) {
						try {
							document.cookie =
								'_hjOptOut=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' +
								window.location.hostname +
								';';
							document.cookie =
								'_hjOptOut=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

							if (typeof localStorage !== 'undefined') {
								localStorage.removeItem('_hjOptOut');
							}

							if (typeof window.hj.optIn === 'function') {
								window.hj.optIn();
							}
						} catch (error) {
							// Silently handle opt-out clearing errors
						}
					}

					// Always send initial stateChange - Hotjar will decide if user is in sample
					// Without this, Hotjar won't record anything on the initial page load
					window.hj('stateChange', window.location.href);
				}
			}, 2000);
		}
	}, []);

	// Send periodic status updates to Hotjar
	useEffect(() => {
		if (!state.isRecording || !window.hj) {
			return;
		}

		const interval = setInterval(() => {
			const duration = Date.now() - state.sessionStartTime;
			window.hj('event', 'recording-active', {
				sessionDuration: duration,
			});
		}, PERIODIC_STATUS_INTERVAL);

		return () => {
			clearInterval(interval);
		};
	}, [state.isRecording, state.sessionStartTime]);

	if (NEXT_CONFIG.PRODUCTION) {
		return null;
	}

	return <StyledHotjarManager $isRecording={state.isRecording} />;
};

const StyledHotjarManager = createGlobalStyle<{
	theme: ThemeType;
	$isRecording: boolean;
}>`
	${(props) =>
		props.$isRecording
			? css`
					.header {
						background-color: orange !important;
					}
			  `
			: ''}
`;

export default HotjarManager;
